import { ColumnDef } from "@tanstack/react-table";
import { useLanguage, useSearchParameter } from "hooks";
import { OrderPaymentMethods } from "modules/orders";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ItemsTable, TableItemStatus } from "ui";

import {
  TRANSACTION_ID_SEARCH_PARAMETER,
  TRANSACTIONS_STATUSES_LIST
} from "../../constants";
import { TransactionItem } from "../../financial.types";
import { TransactionDetailsModal } from "../modals";
import { TransactionsTableProps } from "./TransactionsTable.types";

import styles from "./TransactionsTable.module.scss";

const TransactionsTable = ({
  transactions,
  sorting,
  setSorting,
  isLoading,
  asResellerPayments
}: TransactionsTableProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const [transactionId, setTransactionId] = useSearchParameter(
    TRANSACTION_ID_SEARCH_PARAMETER
  );

  const columns: Array<ColumnDef<TransactionItem, TransactionItem[]>> = useMemo(
    () => [
      {
        id: "id",
        header: t("Transaction"),
        enableSorting: false,
        size: 13,
        minSize: 13,
        cell: ({ row }) => (
          <>
            <p className={styles["transactions__item--id"]}>
              {transactions[row.index].id}
            </p>
            {!asResellerPayments && (
              <p className={styles["transactions__item--user"]}>
                {`${transactions[row.index].reseller.firstname} ${transactions[row.index].reseller.lastname}`}
              </p>
            )}
          </>
        )
      },
      {
        id: "method",
        header: t("Method"),
        enableSorting: false,
        size: 8,
        minSize: 8,
        cell: ({ row }) => {
          const paymentMethod = transactions[row.index].paymentMethod;
          return t(OrderPaymentMethods[paymentMethod].label);
        }
      },
      {
        id: "order",
        header: t("Order"),
        enableSorting: false,
        size: 5,
        minSize: 5,
        cell: ({ row }) => (
          <p className={styles["transactions__item--order"]}>
            {transactions[row.index].orderId}
          </p>
        )
      },
      {
        id: "amount",
        header: t("Total (FCFA)"),
        size: 8,
        minSize: 8,
        cell: ({ row }) =>
          transactions[row.index].amount.toLocaleString("en-US")
      },
      {
        id: "createdAt",
        header: t("Date & Time"),
        size: 5,
        minSize: 5,
        cell: ({ row }) => (
          <pre>
            {`${moment(transactions[row.index].createdAt).format("DD/MM/YYYY")}`}
            {"\n"}
            {`${moment(transactions[row.index].createdAt).format("HH:mm")}`}
          </pre>
        )
      },
      {
        id: "status",
        header: t("Status"),
        enableSorting: false,
        size: 5,
        minSize: 5,
        cell: ({ row }) => (
          <TableItemStatus
            className={styles["transactions__item--status"]}
            statusesList={TRANSACTIONS_STATUSES_LIST}
            activeStatusIndex={TRANSACTIONS_STATUSES_LIST.findIndex(
              (statusItem) =>
                statusItem?.value === transactions[row.index].status
            )}
          />
        )
      }
    ],
    [transactions, asResellerPayments, currentLanguage]
  );

  return (
    <>
      <ItemsTable
        columns={columns}
        data={transactions}
        sorting={sorting}
        skeletonHeight={90}
        onRowClick={(transaction) => setTransactionId(transaction.id)}
        setSorting={setSorting}
        isLoading={isLoading}
        className={styles.transactions__table}
      />

      <TransactionDetailsModal
        isOpen={!!transactionId}
        onClose={() => setTransactionId(null)}
      />
    </>
  );
};

export default TransactionsTable;
